import { useParams } from 'react-router-dom'
import { processCollaborationMilestoneStep } from 'services/api/collaboration'
import React, { useEffect, useMemo, useState } from 'react'

import { toast } from 'react-toastify'
import ChooseFromWebsite from './gigs/product-review/ChooseFromWebsite'
import { Gig, Milestone, MilestoneStep } from 'types/collaboration'
import { Address } from 'types/address'
import { fetchPlace } from 'services/api/address'
import inModal from 'utils/hoc/inModal'
import { formatAmount } from 'utils/helpers'
import { RootState } from 'store'
import { useSelector } from 'react-redux'
import NearbyStores from './gigs/store-visit/NearbyStores'

interface GigAdditionProps {
    gigs: Gig[]
    activeMilestone: Milestone
    isActive: boolean,
    onClose: (refetch?: boolean) => void,
    onPlayButtonClick: (gig: Gig) => void,
    handleDownload: (gig: Gig) => void,
    step: MilestoneStep
}

const GigAddition: React.FC<GigAdditionProps> = ({ activeMilestone, gigs, step, onPlayButtonClick, handleDownload, onClose }) => {

    const { campaignId, collaborationId, milestoneId } = useParams();

    const [selectedGigs, setSelectedGigs] = useState<number[]>([]);
    const [places, setPlaces] = useState<Record<string, any>>({});
    const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
    const [address, setAddress] = useState<Address | null>(null);
    const [name, setName] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [selectedProductGigs, setSelectedProductGigs] = useState<Gig[]>([]);

    const hubId = useSelector((state: RootState) => state.auth.selectedHubId);

    const getCity = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('administrative_area_level_3'));
        return component?.longText ?? ''
    }

    const getRegion = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('administrative_area_level_1'));
        return component?.longText ?? '';
    }

    const getPincode = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('postal_code'));
        return component?.longText ?? '';
    }

    const getSublocality = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('sublocality'));
        return component?.longText ?? '';
    }


    const fetchSelectedPlace = async (placeId: string) => {
        if (!hubId) return;
        try {
            const place = await fetchPlace(hubId, placeId);
            setPlaces((prev) => ({ ...prev, [placeId]: place }))

        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (selectedPlaceId && places[selectedPlaceId]) {
            const place = places[selectedPlaceId];
            setName(place.displayName?.text ?? '');
            setAddress({
                name: '',
                city: getCity(place),
                region: getRegion(place),
                sublocality: getSublocality(place),
                pincode: getPincode(place),
                premise: '',
                lat: place.location.latitude,
                lng: place.location.longitude,
                place_id: selectedPlaceId
            })
        } else if (selectedPlaceId) {
            fetchSelectedPlace(selectedPlaceId);
        }
    }, [selectedPlaceId, places])

    const gigType = useMemo(() => {
        return activeMilestone?.gig_type?.slug;
    }, [activeMilestone])


    const onSubmit = async () => {
        if (!campaignId || !collaborationId || !milestoneId || !hubId) return;
        if (gigType === 'store_visit' && (!address || !name)) return;
        if (gigType === 'product_review' && (selectedProductGigs.length !== activeMilestone?.maximum_gigs)) return;
        try {
            setSubmitting(true);
            let properties: any = {};
            if (gigType === 'store_visit' && address && name) {
                properties = {
                    "properties[address][name]": name,
                    "properties[address][city]": address.city,
                    "properties[address][region]": address.region,
                    "properties[address][sublocality]": address.sublocality,
                    "properties[address][pincode]": address.pincode,
                    "properties[address][lat]": address.lat,
                    "properties[address][lng]": address.lng,
                    "properties[address][place_id]": address.place_id,
                }
            }
            if (gigType === 'product_review' && selectedProductGigs.length === activeMilestone?.maximum_gigs) {
                for (let i in selectedProductGigs) {
                    const gig = selectedProductGigs[i];
                    properties[`products[${i}][name]`] = gig.name;
                    properties[`products[${i}][value]`] = gig.value;
                    properties[`products[${i}][thumbnail_url]`] = gig.thumbnail_url;
                    // properties[`products[${i}][properties][description]`] = gig.properties.description;
                    properties[`products[${i}][properties][url]`] = gig.properties.url;
                }
            }

            await processCollaborationMilestoneStep(hubId, campaignId, collaborationId, milestoneId, { step: step.gig_step.slug, ...(name ? { name } : {}), ...properties });
            onClose(true);
        } catch (error: any) {
            console.log(error);
            toast.error(error?.response?.data?.message ?? "Unknown error occurred.")
        } finally {
            setSubmitting(false);
        }
    }

    const handleSelectedProductsFromWebsite = (products: any[]) => {
        setSelectedProductGigs(products);
    }

    return (
        <div className='scrollable-modal-body'>
            {gigType === 'product_review' && (
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center justify-content-start gap-2">
                        <span className="fw-bold text-dark">Products To Be Added:</span>
                        <span className="fs-12">{activeMilestone?.maximum_gigs}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-2">
                        <span className="fw-bold text-dark">Total Amount:</span>
                        <span className="fs-12">{formatAmount(selectedProductGigs.reduce((acc: number, gig: Gig) => acc + Number(gig.value ?? 0), 0))}</span>
                    </div>
                </div>
            )
            }
            {
                activeMilestone?.gig_type?.slug === 'store_visit' &&
                <NearbyStores
                    query={activeMilestone?.specifications?.store_name ?? ''}
                    onChange={(placeId) => {
                        setAddress(null);
                        setName('');
                        setSelectedPlaceId(placeId);
                    }}
                />
            }
            {
                activeMilestone?.gig_type?.slug === 'product_review' &&
                <ChooseFromWebsite onSelection={handleSelectedProductsFromWebsite} activeMilestone={activeMilestone} />
            }
            <div className="milestone-step-actions">
                <button className="theme-btn theme-btn-primary-outlined" onClick={() => onClose()}>Cancel</button>
                <button className="theme-btn theme-btn-primary" disabled={submitting || (gigType === 'store_visit' && (!address || !name)) || (gigType === 'product_review' && (selectedProductGigs.length !== activeMilestone?.maximum_gigs))} onClick={onSubmit}>Submit</button>
            </div>
        </div>

    )
}

const GigAdditionModal: React.FC<GigAdditionProps> = (props) => {
    const { activeMilestone } = props
    const title = activeMilestone?.gig_type?.slug === 'store_visit' ? `Find Nearby ${activeMilestone.specifications?.store_name || 'Store'}` : `Add ${activeMilestone.gig_type.object_name || 'Item'}${activeMilestone?.maximum_gigs > 1 ? 's' : ''}`

    const ModalComponent = useMemo(() => inModal(GigAddition, title), [title])

    const headerRightComponent = useMemo(() => {
        if (activeMilestone?.gig_type?.slug !== 'product_review' || !activeMilestone.specifications?.website_url) return null;
        return (
            <div className='d-flex justify-content-end pe-3'>
                <a className="ms-auto text-primary" href={activeMilestone.specifications?.website_url} target="_blank" rel="noopener noreferrer">
                    <i className="ri-external-link-line"></i> Explore
                </a>
            </div>
        )
    }, [activeMilestone])

    const description = useMemo(() => {
        if (activeMilestone?.gig_type?.slug !== 'product_review') return null;
        return (
            <p className='fs-12'>Explore the website and copy the product URL and paste it here to add products to your collaboration.</p>
        )
    }, [activeMilestone])

    return <ModalComponent remount={false} {...props} headerRightComponent={headerRightComponent} description={description} />
}


export default GigAdditionModal;